import { useEffect, useState } from 'react';

import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import Button from '@mui/material/Button';

import useTableContext from '../../../admin-portal/BaseTabsRouting/Headers/HeaderGrid/useTableContext';
import CreateButton from '../../CreateButton/CreateButton';
import GridHeader from './styled/GridHeader';

import type { SxProps } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import type { Dispatch, FC, PropsWithChildren, ReactNode } from 'react';
import type { AdditionalButton, CustomDataGridProps } from '../DataGrid.types';

type Props = {
  rowSelectionModel: GridRowSelectionModel;
  setShowDeleteConfirmation: Dispatch<boolean>;
  setDeleteSelection: Dispatch<boolean>;
  onAddClickTitle?: string;
  additionalButtons?: AdditionalButton[];
  onAddClick?: () => void;
  onDeleteClick?: CustomDataGridProps['onDeleteClick'];
  disableActions?: boolean;
  checkboxSelection?: boolean;
  title?: string | ReactNode;
  headerContent?: ReactNode;
  sx?: SxProps;
};

const AddAdditionalButtons: FC<{ additionalButtons: AdditionalButton[] }> = ({
  additionalButtons
}) => {
  'use memo';

  return additionalButtons.map((additionalButton) => (
    <Button
      startIcon={additionalButton.startIcon ?? null}
      key={additionalButton.label}
      variant="contained"
      onClick={additionalButton.onClick}
      disabled={additionalButton.disabled || false}
      color={additionalButton.color}
    >
      {additionalButton.label}
    </Button>
  ));
};

const DataGridHeader = ({
  onAddClickTitle,
  additionalButtons,
  onAddClick,
  onDeleteClick,
  title,
  rowSelectionModel,
  setShowDeleteConfirmation,
  setDeleteSelection,
  disableActions,
  checkboxSelection,
  headerContent,
  sx
}: Props) => {
  'use memo';

  const { t } = useTranslation('common');

  return (
    <GridHeader sx={sx}>
      <Box sx={{ display: 'flex' }}>
        {checkboxSelection && onDeleteClick && !disableActions && (
          <Button
            variant="contained"
            onClick={() => {
              setDeleteSelection(true);
              setShowDeleteConfirmation(true);
            }}
            disabled={rowSelectionModel.ids.size === 0}
          >
            {t('deleteSelection')}
          </Button>
        )}
        {title && !checkboxSelection && (
          <Typography variant="h6">{title}</Typography>
        )}
        {headerContent}
      </Box>
      <Box ml="auto" display="flex" gap={1}>
        {onAddClick && !disableActions && (
          <CreateButton onClick={onAddClick} label={onAddClickTitle} />
        )}
        {!disableActions && additionalButtons && (
          <AddAdditionalButtons additionalButtons={additionalButtons} />
        )}
      </Box>
    </GridHeader>
  );
};

export default DataGridHeader;

export const DataGridHeaderPortal: FC<PropsWithChildren<object>> = (props) => {
  'use memo';

  const { contentNodeForButtons } = useTableContext();
  const [loaded, setLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (contentNodeForButtons?.current) setLoaded(true);
  }, [contentNodeForButtons]);

  return loaded
    ? createPortal(
        <>{props.children}</>,
        contentNodeForButtons.current as HTMLDivElement
      )
    : null;
};
